const { storage } = require("@/config/firebase");
var pdfjsLib = require('pdfjs-dist-for-node');
const { getMp4Rotation } = require("@/util/mp4_rotation");

// Extension for the stored object, derived from the original name (or MIME as a
// fallback). Kept so URL-based type detection + thumbnailing still work — the
// object name itself is a UUID so the real filename is never exposed.
function extensionFor(file) {
  const name = file && file.name ? String(file.name) : '';
  const dot = name.lastIndexOf('.');
  if (dot > 0 && dot < name.length - 1) {
    const ext = name.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, '');
    if (ext) return ext;
  }
  const mimeMap = {
    'image/jpeg': 'jpg', 'image/png': 'png', 'image/gif': 'gif', 'image/webp': 'webp',
    'video/mp4': 'mp4', 'video/quicktime': 'mov', 'audio/mpeg': 'mp3',
    'application/pdf': 'pdf',
  };
  return mimeMap[(file && file.type) || ''] || 'bin';
}

function randomId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'f-' + Date.now() + '-' + Math.random().toString(36).slice(2, 12);
}

function uploadFileToStorage(file, category = 'messages', tenantId = null) {
  return new Promise((resolve, reject) => {
    // Metadata con tipo MIME para que Firebase lo reconozca correctamente
    const metadata = { contentType: file.type || 'application/octet-stream' };
    // Tenant-scoped, category-first object layout: {category}/{tenantId}/{uuid}.{ext}.
    // The filename is a random UUID (not the real filename) so paths can't be
    // guessed (dictionary attack) and don't leak the original name; combined with
    // the create-only storage rule this also prevents overwriting an existing
    // object. Reads use the tokenized download URL returned below, which resolves
    // regardless of path. Fall back to a flat (still UUID) path if tenantId is
    // missing so an upload never breaks.
    const objectName = `${randomId()}.${extensionFor(file)}`;
    const fileName = tenantId
      ? `${category}/${tenantId}/${objectName}`
      : `${category}/${objectName}`;
    const ref = storage.ref(fileName);
    const uploadTask = ref.put(file, metadata);

    uploadTask.on('state_changed',
        snapshot => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log(`Upload is ${progress}% done`);
        },
        error => {
          console.error('Error uploading file:', error);
          reject(error);
        },
        async () => {
          try {
            const url = await uploadTask.snapshot.ref.getDownloadURL();
            resolve(url);
          } catch (err) {
            reject(err);
          }
        }
    );
  });
}

module.exports = {
  // Render a poster thumbnail from the first frame of a video.
  //
  // If the MP4 carries a tkhd rotation matrix that the browser DIDN'T honor
  // (videoWidth/Height still reflect the pre-rotation frame), we rotate the
  // canvas so the saved thumbnail ends up right-side up. If the browser DID
  // honor it, we draw the frame as-is and skip the rotation.
  loadImageFromVideo: async url => {
    const rotationInfo = await getMp4Rotation(url).catch(() => null);
    const targetRotation = rotationInfo ? rotationInfo.rotation : 0;
    const displayW = rotationInfo ? rotationInfo.displayWidth : 0;
    const displayH = rotationInfo ? rotationInfo.displayHeight : 0;

    return new Promise((resolve, reject) => {
      let video = document.createElement('video');
      video.crossOrigin = "anonymous";
      video.onloadedmetadata = function() {
        this.currentTime = 0;
      };
      video.onseeked = () => {
        const vw = video.videoWidth;
        const vh = video.videoHeight;
        // For 90°/270° rotations the dim-equality check is a reliable signal
        // that the browser already applied the matrix. For 180°, pre- and
        // post-rotation dims are identical so the check is a false positive
        // and we have to fall back to environment knowledge: real browsers
        // honor 180°, Electron's bundled Chromium does not.
        const dimsMatch =
          targetRotation !== 0 && displayW > 0 && displayH > 0 &&
          (Math.abs(vw - displayW) <= 2 && Math.abs(vh - displayH) <= 2);
        let browserHonored;
        if (targetRotation === 180) {
          const isElectron = /electron/i.test((typeof navigator !== 'undefined' && navigator.userAgent) || '');
          browserHonored = !isElectron;
        } else {
          browserHonored = dimsMatch;
        }
        const rotation = browserHonored ? 0 : targetRotation;

        const canvas = document.createElement('canvas');
        if (rotation === 90 || rotation === 270) {
          canvas.width = vh;
          canvas.height = vw;
        } else {
          canvas.width = vw;
          canvas.height = vh;
        }
        const ctx = canvas.getContext('2d');
        if (rotation) {
          ctx.save();
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.rotate((rotation * Math.PI) / 180);
          ctx.drawImage(video, -vw / 2, -vh / 2, vw, vh);
          ctx.restore();
        } else {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        }
        setTimeout(() => { window.EventBus.$emit("onUpdateMessages") }, 100);
        resolve(canvas.toDataURL());
      };
      video.onerror = reject;
      video.src = url;
    });
  },

  loadImageFromPdf: url => {
    return new Promise((resolve, reject) => {
      pdfjsLib.getDocument({ url }).promise
          .then(async pdf => {
            let page = await pdf.getPage(1);
            let scale = 1.5;
            let viewport = page.getViewport({ scale });
            let canvas = document.createElement('canvas');
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            let context = canvas.getContext('2d');
            await page.render({ canvasContext: context, viewport }).promise;
            resolve(canvas.toDataURL());
          })
          .catch(reject);
    });
  },

  // Detectar videos usando MIME o extensión
  isVideo(file) {
    return file.type?.startsWith('video/') ||
        (file.url && /\.(mp4|mov|wmv|avi|mkv|m4v)$/i.test(file.url));
  },

  // Detectar imágenes usando MIME o extensión
  isImage(file) {
    return file.type?.startsWith('image/') ||
        (file.url && /\.(jpg|jpeg|png|bmp|tif|tiff|gif)$/i.test(file.url));
  },

  // Detectar PDFs usando MIME o extensión
  isPdf(file) {
    return file.type === 'application/pdf' ||
        (file.url && /\.pdf$/i.test(file.url));
  },

  uploadFileToStorage
};
